import { UseMutationResult } from '@tanstack/react-query';

import { useRevokeAccessToken } from '../hooks/access-tokens/revoke-access-token';
import { useDeleteArea } from '../hooks/areas/delete-area';
import { useDeleteClient } from '../hooks/clients/delete-client';
import { useDeleteCompany } from '../hooks/companies/delete-company';
import { useDeleteDevice } from '../hooks/devices/delete-device';
import { useDeleteDeviceExport } from '../hooks/devices/export/delete-export';
import { useDeleteDeviceImport } from '../hooks/devices/import/delete-import';
import { useDeleteGateway } from '../hooks/gateways/delete-gateway';
import { useDeleteGroup } from '../hooks/groups/delete-group';
import { useDeleteInputWebhook } from '../hooks/input-webhooks/delete-input-webhook';
import { useDeletePlan } from '../hooks/plans/delete-plan';
import { useRevokeRefreshToken } from '../hooks/refresh-tokens/revoke-refresh-token';
import { useDeleteReport } from '../hooks/reports/delete-report';
import { useDeleteRole } from '../hooks/roles/delete-role';
import { useDeleteUser } from '../hooks/users/delete-user';

type TEntities = {
  entitySingular:
    | 'access token'
    | 'area'
    | 'client'
    | 'communication'
    | 'company'
    | 'contact'
    | 'dashboard'
    | 'device'
    | 'device action'
    | 'device frame'
    | 'device import'
    | 'device export'
    | 'action type'
    | 'event type'
    | 'config type'
    | 'configuration type'
    | 'error type'
    | 'flow'
    | 'flow action'
    | 'flow trigger'
    | 'device frame'
    | 'gateway frame'
    | 'metric type'
    | 'passportization field'
    | 'passportization template'
    | 'state type'
    | 'device type'
    | 'device profile'
    | 'gateway'
    | 'getting started'
    | 'group'
    | 'input webhook'
    | 'plan'
    | 'refresh token'
    | 'resource'
    | 'report'
    | 'report log'
    | 'role'
    | 'settings'
    | 'user'
    | 'login log'
    | 'multicast group action';
  entityPlural:
    | 'access tokens'
    | 'areas'
    | 'clients'
    | 'communication'
    | 'companies'
    | 'contacts'
    | 'dashboard'
    | 'devices'
    | 'device actions'
    | 'device frames'
    | 'action types'
    | 'event types'
    | 'config types'
    | 'configuration types'
    | 'error types'
    | 'flows'
    | 'flow actions'
    | 'flow triggers'
    | 'device frames'
    | 'gateway frames'
    | 'metric types'
    | 'passportization fields'
    | 'passportization templates'
    | 'state types'
    | 'device types'
    | 'device profiles'
    | 'gateways'
    | 'groups'
    | 'getting started'
    | 'input webhooks'
    | 'plans'
    | 'refresh tokens'
    | 'resources'
    | 'reports'
    | 'report logs'
    | 'roles'
    | 'settings'
    | 'users'
    | 'login logs'
    | 'multicast group actions';
};

type TPageHeader = {
  path: string;
  entitySingular: TEntities['entitySingular'];
  entityPlural: TEntities['entityPlural'];
  key: string;
  basicActions:
    | (
        | 'create'
        | 'createMain'
        | 'createExtra'
        | 'serverTime'
        | 'delete'
        | 'clone'
        | 'edit'
        | 'update'
        | 'revoke'
        | 'config'
        | 'sendPasswordReset'
        | 'changePassword'
        | 'import'
        | 'export'
      )[]
    | null;
  bulkActions: ('control' | 'setGroup' | 'setPlan' | 'setDeviceProfile' | 'delete')[] | null;
  deleteFn:
    | null
    | ((id?: string) => {
        isError: boolean;
        isLoading: boolean;
        mutate: UseMutationResult<void, unknown, any, unknown>['mutate'];
      });
  cloneAvailable: boolean;
};

const getEntityContent = (type: TEntities['entitySingular']): TPageHeader => {
  let path, entityPlural: TEntities['entityPlural'], entitySingular: TEntities['entitySingular'];
  let key: TPageHeader['key'];
  let basicActions: TPageHeader['basicActions'];
  let bulkActions: TPageHeader['bulkActions'];
  let deleteFn: TPageHeader['deleteFn'];
  let cloneAvailable: TPageHeader['cloneAvailable'];

  switch (type) {
    case 'access token':
      path = '/settings/access-tokens';
      key = 'access-token';
      entityPlural = 'access tokens';
      entitySingular = 'access token';
      basicActions = ['revoke'];
      bulkActions = null;
      deleteFn = () => useRevokeAccessToken();
      cloneAvailable = false;
      break;
    case 'area':
      path = '/areas';
      key = 'area';
      entityPlural = 'areas';
      entitySingular = 'area';
      basicActions = ['create', 'clone', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = () => useDeleteArea();
      cloneAvailable = false;
      break;
    case 'client':
      path = '/settings/clients';
      key = 'client';
      entityPlural = 'clients';
      entitySingular = 'client';
      basicActions = ['create', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = () => useDeleteClient();
      cloneAvailable = false;
      break;
    case 'communication':
      path = '/settings/communication';
      key = 'communication';
      entityPlural = 'communication';
      entitySingular = 'communication';
      basicActions = [];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'company':
      path = '/settings/companies';
      key = 'company';
      entityPlural = 'companies';
      entitySingular = 'company';
      basicActions = ['create', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = () => useDeleteCompany();
      cloneAvailable = false;
      break;
    case 'contact':
      path = '/settings/companies/';
      key = 'contact';
      entityPlural = 'contacts';
      entitySingular = 'contact';
      basicActions = [];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'device':
      path = '/devices';
      key = 'device';
      entityPlural = 'devices';
      entitySingular = 'device';
      basicActions = ['import', 'export', 'create', 'clone', 'delete', 'config', 'edit', 'update'];
      bulkActions = null;
      deleteFn = () => useDeleteDevice();
      cloneAvailable = true;
      break;
    case 'device action':
      path = '/devices';
      key = 'device-action';
      entityPlural = 'device actions';
      entitySingular = 'device action';
      basicActions = [];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'device frame':
      path = '/';
      key = 'device-frame';
      entityPlural = 'device frames';
      entitySingular = 'device frame';
      basicActions = [];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'device import':
      path = '/';
      key = 'device-import';
      entityPlural = 'devices';
      entitySingular = 'device import';
      basicActions = [];
      bulkActions = null;
      deleteFn = () => useDeleteDeviceImport();
      cloneAvailable = false;
      break;
    case 'device export':
      path = '/';
      key = 'device-export';
      entityPlural = 'devices';
      entitySingular = 'device export';
      basicActions = [];
      bulkActions = null;
      deleteFn = () => useDeleteDeviceExport();
      cloneAvailable = false;
      break;
    case 'flow':
      path = '/settings/flows';
      key = 'flow';
      entityPlural = 'flows';
      entitySingular = 'flow';
      basicActions = ['create', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'flow action':
      path = '/settings/flow-actions';
      key = 'flow-action';
      entityPlural = 'flow actions';
      entitySingular = 'flow action';
      basicActions = ['create', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'flow trigger':
      path = '/settings/flow-triggers';
      key = 'flow-trigger';
      entityPlural = 'flow triggers';
      entitySingular = 'flow trigger';
      basicActions = ['create', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'gateway frame':
      path = '/';
      key = 'gateway-frame';
      entityPlural = 'gateway frames';
      entitySingular = 'gateway frame';
      basicActions = [];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'config type':
      path = '/settings/configs';
      key = 'config-type';
      entityPlural = 'config types';
      entitySingular = 'config type';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'configuration type':
      path = '/settings/configurations';
      key = 'configuration-type';
      entityPlural = 'configuration types';
      entitySingular = 'configuration type';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'error type':
      path = '/settings/errors';
      key = 'error-type';
      entityPlural = 'error types';
      entitySingular = 'error type';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'action type':
      path = '/settings/actions';
      key = 'action-type';
      entityPlural = 'action types';
      entitySingular = 'action type';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'event type':
      path = '/settings/events';
      key = 'event-type';
      entityPlural = 'event types';
      entitySingular = 'event type';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'metric type':
      path = '/settings/metrics';
      key = 'metric-type';
      entityPlural = 'metric types';
      entitySingular = 'metric type';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'passportization field':
      path = '/settings/passportization-fields';
      key = 'passportization-field';
      entityPlural = 'passportization fields';
      entitySingular = 'passportization field';
      basicActions = ['edit', 'update'];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'passportization template':
      path = '/settings/passportization-templates';
      key = 'passportization-template';
      entityPlural = 'passportization templates';
      entitySingular = 'passportization template';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'state type':
      path = '/settings/states';
      key = 'state-type';
      entityPlural = 'state types';
      entitySingular = 'state type';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'device type':
      path = '/settings/device-types';
      key = 'device-type';
      entityPlural = 'device types';
      entitySingular = 'device type';
      basicActions = ['edit', 'update'];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'device profile':
      path = '/device-profiles';
      key = 'device-profile';
      entityPlural = 'device profiles';
      entitySingular = 'device profile';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'input webhook':
      path = '/settings/input-webhooks';
      key = 'input-webhook';
      entityPlural = 'input webhooks';
      entitySingular = 'input webhook';
      basicActions = ['create', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = () => useDeleteInputWebhook();
      cloneAvailable = false;
      break;
    case 'gateway':
      path = '/gateways';
      key = 'gateway';
      entityPlural = 'gateways';
      entitySingular = 'gateway';
      basicActions = ['create', 'clone', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = () => useDeleteGateway();
      cloneAvailable = true;
      break;
    case 'group':
      path = '/groups';
      key = 'subgroup';
      entityPlural = 'groups';
      entitySingular = 'group';
      basicActions = ['create', 'clone', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = () => useDeleteGroup();
      cloneAvailable = false;
      break;
    case 'plan':
      path = '/daily-plans';
      key = 'plan';
      entityPlural = 'plans';
      entitySingular = 'plan';
      basicActions = ['createMain', 'createExtra', 'clone', 'delete', 'edit', 'update', 'create'];
      bulkActions = null;
      deleteFn = () => useDeletePlan();
      cloneAvailable = false;
      break;
    case 'refresh token':
      path = '/settings/refresh-tokens';
      key = 'refresh-token';
      entityPlural = 'refresh tokens';
      entitySingular = 'refresh token';
      basicActions = ['revoke'];
      bulkActions = null;
      deleteFn = () => useRevokeRefreshToken();
      cloneAvailable = false;
      break;
    case 'resource':
      path = '/settings/resources';
      key = 'resource';
      entityPlural = 'resources';
      entitySingular = 'resource';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'report':
      path = '/reports';
      key = 'report';
      entityPlural = 'reports';
      entitySingular = 'report';
      basicActions = ['delete', 'edit', 'update', 'create'];
      bulkActions = null;
      deleteFn = () => useDeleteReport();
      cloneAvailable = false;
      break;
    case 'report log':
      path = '/reports/';
      key = 'report-log';
      entityPlural = 'report logs';
      entitySingular = 'report';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'role':
      path = '/settings/roles';
      key = 'role';
      entityPlural = 'roles';
      entitySingular = 'role';
      basicActions = ['create', 'delete', 'edit', 'update'];
      bulkActions = null;
      deleteFn = () => useDeleteRole();
      cloneAvailable = false;
      break;
    case 'user':
      path = '/settings/users';
      key = 'user';
      entityPlural = 'users';
      entitySingular = 'user';
      basicActions = ['create', 'sendPasswordReset', 'delete', 'edit', 'update', 'changePassword'];
      bulkActions = null;
      deleteFn = () => useDeleteUser();
      cloneAvailable = false;
      break;
    case 'settings':
      path = '/settings';
      key = 'settings';
      entityPlural = 'settings';
      entitySingular = 'settings';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'login log':
      path = '/settings/login-logs';
      key = 'login-log';
      entityPlural = 'login logs';
      entitySingular = 'login log';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    case 'multicast group action':
      path = '/groups';
      key = 'multicast-group-action';
      entityPlural = 'multicast group actions';
      entitySingular = 'multicast group action';
      basicActions = null;
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
    default:
      path = 'dashboard';
      key = 'dashboard';
      entityPlural = 'dashboard';
      entitySingular = 'dashboard';
      basicActions = ['serverTime'];
      bulkActions = null;
      deleteFn = null;
      cloneAvailable = false;
      break;
  }

  return { path, entityPlural, entitySingular, key, basicActions, bulkActions, deleteFn, cloneAvailable };
};

export { getEntityContent, TEntities };
